import axios from 'axios';

const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (!envUrl) return '';
  try {
    const envHost = new URL(envUrl).hostname;
    const currentHost = window.location.hostname;
    if (envHost !== currentHost && currentHost !== 'localhost') {
      return '';
    }
  } catch {
    // URL parse error, use as-is
  }
  return envUrl;
};

const API_URL = getBaseUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      // Redirect to login if on admin page
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API functions
export const locationApi = {
  getAll: () => api.get('/locations'),
  getBySlug: (slug) => api.get(`/locations/${slug}`),
  checkDeliveryZone: (postalCode, locationId, subtotal = 0) => 
    api.get('/locations/check-delivery-zone', { params: { postal_code: postalCode, location_id: locationId, subtotal } }),
  isOpen: (slug) => api.get(`/locations/${slug}/is-open`),
  getDeliveryFeeInfo: (slug) => api.get(`/locations/${slug}/delivery-fee-info`),
  getAnnouncements: () => api.get('/locations/ankuendigungen'),
  eroeffnungInteresse: (email, filiale) => api.post('/locations/ankuendigungen/interesse', { email, filiale }),
  getGooglePlacesKey: () => api.get('/locations/config/google-places-key'),
};

export const checkoutApi = {
  validateAddress: (data) => api.post('/checkout/validate-address', data),
};

export const routingApi = {
  byPlz: (plz) => api.post('/routing/plz', { plz }),
  wechsel: (filialeId, itemIds) => api.post('/routing/wechsel', { filiale_id: filialeId, item_ids: itemIds }),
};

export const authApi = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

export const menuApi = {
  getMenu: (params) => api.get('/menu', { params }),
  getCategories: (locationId) => api.get('/menu/categories', { params: { location_id: locationId } }),
  getItem: (itemId) => api.get(`/menu/item/${itemId}`),
  getFeatured: (locationId) => api.get('/menu/featured', { params: { location_id: locationId } }),
  getModifierGroups: (ids) => api.get('/menu/modifier-groups', { params: { ids: ids?.join(',') } }),
};

export const orderApi = {
  create: (orderData) => api.post('/orders', orderData),
  getStatus: (orderNumber) => api.get(`/orders/status/${orderNumber}`),
  getMyOrders: (phone) => api.get('/orders/my-orders', { params: { phone } }),
  reorder: (orderNumber) => api.post(`/orders/reorder/${orderNumber}`),
};

export const paymentApi = {
  getPayPalClientId: (locationId) => api.get(`/payments/paypal/client-id/${locationId}`),
  createPayPalOrder: (data) => api.post('/payments/paypal/create-order', data),
  capturePayPalOrder: (data) => api.post('/payments/paypal/capture-order', data),
};

export const customerApi = {
  recognize: (token) => api.post('/customer/recognize', { token }),
  save: (data) => api.post('/customer/save', data),
  requestMagicLink: (email) => api.post('/customer/magic-link', { email }),
  verifyMagicLink: (token) => api.post('/customer/verify-magic-link', { token }),
  getOrders: (customerId, token) => api.get(`/customer/orders/${customerId}`, { params: { token } }),
  addAddress: (customerId, data) => api.post(`/customer/addresses/${customerId}`, data),
  updateAddress: (customerId, addressId, data) => api.put(`/customer/addresses/${customerId}/${addressId}`, data),
  setDefaultAddress: (customerId, addressId, token) => api.put(`/customer/addresses/${customerId}/${addressId}/default`, { token }),
  deleteAddress: (customerId, addressId, token) => api.delete(`/customer/addresses/${customerId}/${addressId}`, { params: { token } }),
  deleteAllData: (customerId, token) => api.delete(`/customer/data/${customerId}`, { data: { token } }),
};

export const loyaltyApi = {
  getBalance: (token) => api.get('/loyalty/balance', { params: { token } }),
  getTransactions: (token) => api.get('/loyalty/transactions', { params: { token } }),
  deleteAccount: (token) => api.delete('/loyalty/account', { params: { token } }),
  claimOrder: (data) => api.post('/loyalty/claim-order', data),
};

export const complaintApi = {
  getOrder: (orderNumber, email) => api.get(`/complaints/order/${orderNumber}`, { params: { email } }),
  submit: (formData) => api.post('/complaints', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const siteApi = {
  getPublicSettings: () => api.get('/settings/public'),
};

export const discountApi = {
  validate: (data) => api.post('/discounts/validate', data),
  getDailyDeals: (locationId) => api.get('/discounts/daily-deals', { params: { location_id: locationId } }),
  getAllDailyDeals: () => api.get('/discounts/daily-deals/all'),
  getPickupDiscountInfo: (subtotal) => api.get('/discounts/pickup-discount-info', { params: { subtotal } }),
};

export const reviewsApi = {
  getReviews: () => api.get('/reviews'),
};

export const kioskApi = {
  getScreensaver: () => api.get('/kiosk/screensaver'),
};

export const adminApi = {
  // Dashboard
  getStats: (params) => api.get('/admin/dashboard/stats', { params }),
  getLoyaltyStats: () => api.get('/admin/loyalty/stats'),
  // Orders
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrderStatus: (id, data) => api.patch(`/admin/orders/${id}/status`, data),
  cancelOrder: (id, data) => api.post(`/admin/orders/${id}/cancel`, data),
  sendToPOS: (id) => api.post(`/admin/orders/${id}/send-to-expertorder`),
  // Menu
  getMenuItems: (params) => api.get('/admin/menu/items', { params }),
  createMenuItem: (data) => api.post('/admin/menu/items', data),
  updateMenuItem: (id, data) => api.put(`/admin/menu/items/${id}`, data),
  toggleAvailability: (id, isAvailable) => api.patch(`/admin/menu/items/${id}/availability`, null, { params: { is_available: isAvailable } }),
  deleteMenuItem: (id) => api.delete(`/admin/menu/items/${id}`),
  uploadProductImage: (formData) => api.post('/admin/menu/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getCategories: () => api.get('/admin/menu/categories'),
  createCategory: (data) => api.post('/admin/menu/categories', data),
  updateCategory: (id, data) => api.put(`/admin/menu/categories/${id}`, data),
  reorderCategories: (order) => api.post('/admin/menu/categories/reorder', order),
  reorderItems: (order) => api.post('/admin/menu/items/reorder', order),
  // Modifier Groups
  getModifierGroups: () => api.get('/admin/menu/modifier-groups'),
  createModifierGroup: (data) => api.post('/admin/menu/modifier-groups', data),
  updateModifierGroup: (id, data) => api.put(`/admin/menu/modifier-groups/${id}`, data),
  deleteModifierGroup: (id) => api.delete(`/admin/menu/modifier-groups/${id}`),
  // POS Resilience
  getPOSHealth: (locationId) => api.get(`/admin/pos/health/${locationId}`),
  getPOSStats: () => api.get('/admin/pos/retry-queue/stats'),
  getFailedOrders: (params) => api.get('/admin/pos/failed-orders', { params }),
  getResolvedOrders: (params) => api.get('/admin/pos/failed-orders/resolved', { params }),
  getFailedOrderDetails: (id) => api.get(`/admin/pos/failed-orders/${id}/details`),
  retryFailedOrder: (id) => api.post(`/admin/pos/failed-orders/${id}/retry`),
  resolveFailedOrder: (id, notes, type) => api.post(`/admin/pos/failed-orders/${id}/resolve`, null, { params: { notes, resolution_type: type || 'manual_override' } }),
  getFailedOrderPrintData: (id) => api.get(`/admin/pos/failed-orders/${id}/print-data`),
  // Settings
  getLocationSettings: () => api.get('/admin/settings/locations'),
  getLocationSetting: (id) => api.get(`/admin/settings/locations/${id}`),
  updateLocationSetting: (id, data) => api.put(`/admin/settings/locations/${id}`, data),
  updateLocationStatus: (id, status) => api.put(`/admin/settings/locations/${id}/status`, { status }),
  getSpecialDays: (id) => api.get(`/admin/settings/locations/${id}/special-days`),
  updateSpecialDays: (id, data) => api.put(`/admin/settings/locations/${id}/special-days`, data),
  // Delivery Zones
  getDeliveryZones: (locationId) => api.get(`/admin/delivery-zones/${locationId}`),
  getZonesMap: () => api.get('/admin/delivery-zones/map/all'),
  createDeliveryZone: (locationId, data) => api.post(`/admin/delivery-zones/${locationId}`, data),
  updateDeliveryZone: (locationId, zoneId, data) => api.put(`/admin/delivery-zones/${locationId}/${zoneId}`, data),
  deleteDeliveryZone: (locationId, zoneId) => api.delete(`/admin/delivery-zones/${locationId}/${zoneId}`),
  checkDeliveryZone: (locationId, postalCode) => api.post(`/admin/delivery-zones/${locationId}/check`, { postal_code: postalCode }),
  getRoutingOverview: () => api.get('/admin/routing/uebersicht'),
  updateRoutingSettings: (slug, data) => api.put(`/admin/routing/filiale/${slug}`, data),
  getUncoveredPlz: () => api.get('/admin/routing/nicht-abgedeckt'),
  // Discount Codes
  getDiscountCodes: (params) => api.get('/admin/discounts', { params }),
  createDiscountCode: (data) => api.post('/admin/discounts', data),
  updateDiscountCode: (id, data) => api.put(`/admin/discounts/${id}`, data),
  deleteDiscountCode: (id) => api.delete(`/admin/discounts/${id}`),
  // Daily Deals
  getDailyDeals: (params) => api.get('/admin/daily-deals', { params }),
  createDailyDeal: (data) => api.post('/admin/daily-deals', data),
  updateDailyDeal: (id, data) => api.put(`/admin/daily-deals/${id}`, data),
  deleteDailyDeal: (id) => api.delete(`/admin/daily-deals/${id}`),
  generateDailyDealImage: (id, data = {}) => api.post(`/admin/daily-deals/${id}/generate-image`, data, { timeout: 180000 }),
  // Global Settings
  getGlobalSettings: () => api.get('/admin/global-settings'),
  updateGlobalSettings: (data) => api.put('/admin/global-settings', data),
  // Send to POS (alias)
  sendOrderToPOS: (id) => api.post(`/admin/orders/${id}/send-to-expertorder`),
  // POS Logs
  getPOSLogs: (params) => api.get('/admin/pos/logs', { params }),
  // Sprint 6–7: cashier management + live dashboard
  getPOSCashiers: () => api.get('/admin/pos/cashiers'),
  createPOSCashier: (body) => api.post('/admin/pos/cashiers', body),
  resetPOSCashierPin: (cashierId, body) => api.post(`/admin/pos/cashiers/${cashierId}/reset-pin`, body),
  setPOSCashierActive: (cashierId, isActive) => api.post(`/admin/pos/cashiers/${cashierId}/active`, { is_active: isActive }),
  getPOSLiveDashboard: (locationId) => api.get('/admin/pos/live-dashboard', { params: locationId ? { location_id: locationId } : {} }),
  // Sprint 9: customer directory (DSGVO)
  getCustomers: (q = '', limit = 100) => api.get('/admin/customers', { params: { q, limit } }),
  getCustomer: (id) => api.get(`/admin/customers/${id}`),
  deleteCustomerDSGVO: (id, reason) => api.delete(`/admin/customers/${id}`, { data: { reason } }),
  getCustomerDeletionCandidates: (years = 3) => api.get('/admin/customers/deletion-candidates', { params: { years } }),
  bulkDeleteCustomers: (customer_ids, reason) => api.post('/admin/customers/bulk-delete', { customer_ids, reason }),
  exportCustomersCSV: (params = {}) => api.get('/admin/customers/export.csv', { params, responseType: 'blob' }),
  // Backups
  getBackups: () => api.get('/admin/backups'),
  triggerBackup: () => api.post('/admin/backups/trigger'),
  verifyBackup: (name) => api.get(`/admin/backups/${name}/verify`),
  downloadBackup: (name) => api.get(`/admin/backups/${name}/download`, { responseType: 'blob' }),
  // Social Media
  getSocialPosts: (params) => api.get('/admin/social/posts', { params }),
  getSocialPost: (id) => api.get(`/admin/social/posts/${id}`),
  createSocialPost: (data) => api.post('/admin/social/posts', data),
  updateSocialPost: (id, data) => api.put(`/admin/social/posts/${id}`, data),
  deleteSocialPost: (id) => api.delete(`/admin/social/posts/${id}`),
  approveSocialPost: (id) => api.post(`/admin/social/posts/${id}/approve`),
  rejectSocialPost: (id) => api.post(`/admin/social/posts/${id}/reject`),
  getSocialTemplates: (params) => api.get('/admin/social/templates', { params }),
  createSocialTemplate: (data) => api.post('/admin/social/templates', data),
  updateSocialTemplate: (id, data) => api.put(`/admin/social/templates/${id}`, data),
  deleteSocialTemplate: (id) => api.delete(`/admin/social/templates/${id}`),
  getSocialSettings: () => api.get('/admin/social/settings'),
  updateSocialSettings: (data) => api.put('/admin/social/settings', data),
  getSocialLibrary: (params) => api.get('/admin/social/library', { params }),
  deleteSocialMedia: (id) => api.delete(`/admin/social/library/${id}`),
  getSocialCalendar: (params) => api.get('/admin/social/calendar', { params }),
  getSocialAnalytics: () => api.get('/admin/social/analytics'),
  seedSocialTemplates: () => api.post('/admin/social/seed'),
  // Social Media Generation (Phase 2)
  startSocialGeneration: (data) => api.post('/admin/social/generate', data),
  getSocialGenerationStatus: (jobId) => api.get(`/admin/social/generate/${jobId}/status`),
  regenerateSocialCaptions: (jobId) => api.post(`/admin/social/generate/${jobId}/regenerate-captions`),
  saveSocialGeneratedPost: (jobId, data) => api.post(`/admin/social/generate/${jobId}/save`, data),
  // Social Media Phase 3: Publishing & Automation
  getSocialReviewQueue: () => api.get('/admin/social/review-queue'),
  approveAllReview: () => api.post('/admin/social/review-queue/approve-all'),
  runSafetyCheck: (id) => api.post(`/admin/social/posts/${id}/safety-check`),
  getInstagramStatus: () => api.get('/admin/social/instagram/status'),
  manualPublish: (id) => api.post(`/admin/social/instagram/publish/${id}`),
  disconnectInstagram: () => api.post('/auth/instagram/disconnect'),
  // Social Media Phase 4: Analytics & Notifications
  getSocialNotifications: (params) => api.get('/admin/social/notifications', { params }),
  markNotificationsRead: () => api.post('/admin/social/notifications/read-all'),
  syncInsights: () => api.post('/admin/social/insights/sync'),
  // Content Engine Approval-System (Phase 1)
  listSocialBatches: (params) => api.get('/admin/social/batches', { params }),
  getCurrentBatch: () => api.get('/admin/social/batches/current'),
  getSocialBatch: (id) => api.get(`/admin/social/batches/${id}`),
  generateTestBatch: () => api.post('/admin/social/batches/generate-test', {}, { timeout: 180000 }),
  generateWeeklyBatch: (data) => api.post('/admin/social/batches/generate', data, { timeout: 300000 }),
  approveAllInBatch: (id) => api.post(`/admin/social/batches/${id}/approve-all`),
  regenerateAllInBatch: (id) => api.post(`/admin/social/batches/${id}/regenerate-all`, {}, { timeout: 300000 }),
  approvePostInBatch: (id) => api.post(`/admin/social/posts/${id}/approve-batch`),
  rejectPostInBatch: (id) => api.post(`/admin/social/posts/${id}/reject-batch`),
  editSocialPost: (id, data) => api.put(`/admin/social/posts/${id}/edit`, data),
  regeneratePostCaption: (id) => api.post(`/admin/social/posts/${id}/regenerate-caption`, {}, { timeout: 90000 }),
  regeneratePostHashtags: (id) => api.post(`/admin/social/posts/${id}/regenerate-hashtags`),
  regeneratePostImage: (id) => api.post(`/admin/social/posts/${id}/regenerate-image`),
  listProductAssets: (params) => api.get('/admin/social/assets/products', { params }),
  // Content Engine — Stories (Phase 1: Video-Stories / Manual Upload)
  listSocialStories: (params) => api.get('/admin/social/stories/list', { params }),
  getSocialStory: (id) => api.get(`/admin/social/stories/${id}`),
  uploadSocialStory: (formData, onProgress) => api.post('/admin/social/stories/manual-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress: onProgress,
  }),
  generateCinemagraph: (data) => api.post('/admin/social/stories/generate-cinemagraph', data, { timeout: 30000 }),
  deleteSocialStory: (id) => api.delete(`/admin/social/stories/${id}`),
  publishStoryNow: (id) => api.post(`/admin/social/stories/${id}/publish-now`, {}, { timeout: 300000 }),
  generateStoryCaption: (id, data = {}) => api.post(`/admin/social/stories/${id}/generate-caption`, data, { timeout: 60000 }),
  applyStoryOverlay: (id, data) => api.post(`/admin/social/stories/${id}/apply-overlay`, data, { timeout: 180000 }),
  // V4 Action-Reels with motion archetypes
  listMotionArchetypes: () => api.get('/admin/social/motion-archetypes'),
  generateV4Reel: (data) => api.post('/admin/social/stories/generate-v4', data, { timeout: 30000 }),
  applyV4Overlay: (id, data) => api.post(`/admin/social/stories/${id}/apply-v4-overlay`, data, { timeout: 180000 }),
  listStylePresets: () => api.get('/admin/social/style-presets'),
  // Weekly plan (Konferenz #31)
  getWeeklyPlan: () => api.get('/admin/social/weekly-plan'),
  updateWeeklyPlan: (weekday, data) => api.put(`/admin/social/weekly-plan/${weekday}`, data),
  generateWeekNow: (weekday) => api.post('/admin/social/weekly-plan/generate-now' + (weekday !== undefined ? `?weekday=${weekday}` : '')),
  getWeeklyLastRun: () => api.get('/admin/social/weekly-plan/last-run'),
  autoFillWeeklyPlan: () => api.post('/admin/social/weekly-plan/auto-fill'),
  // 1-Click: Approve + Schedule complete week (Mo..So)
  approveWeek: (data) => api.post('/admin/social/stories/approve-week', data, { timeout: 30000 }),
  // System Health (Konferenz #50)
  systemHealth: () => api.get('/admin/system/health', { timeout: 5000 }),
  // Margin Reports (Filial-Monatsbericht)
  getMarginFilialen: () => api.get('/admin/margin-reports/filialen'),
  getMarginReport: (filiale, year, month) => api.get('/admin/margin-reports', { params: { filiale, year, month } }),
  downloadMarginPdf: (filiale, year, month) => api.get('/admin/margin-reports/pdf', { params: { filiale, year, month }, responseType: 'blob' }),
  sendMarginReport: (filiale, year, month, opts={}) => api.post('/admin/margin-reports/send', null, { params: { filiale, year, month, ...opts } }),
  getMarginHistory: (filiale) => api.get('/admin/margin-reports/history', { params: filiale ? { filiale } : {} }),
  getMarginRecipients: () => api.get('/admin/margin-reports/recipients'),
  updateMarginRecipient: (filiale, data) => api.put(`/admin/margin-reports/recipients/${filiale}`, data),
  // Vouchers
  listVouchers: () => api.get('/admin/vouchers'),
  createVoucher: (data) => api.post('/admin/vouchers', data),
  voucherPdf: (code) => api.get(`/admin/vouchers/${code}/pdf`, { responseType: 'blob' }),
  voucherRedemptions: (code) => api.get(`/admin/vouchers/${code}/redemptions`),
  voucherDisable: (code) => api.post(`/admin/vouchers/${code}/disable`),
};
